import { DateRange as MaterialDateRange } from '@angular/material/datepicker';
import {
  differenceInMinutes,
  format as fnsFormat,
  Interval,
  isAfter,
  isBefore,
  isEqual,
  isWithinInterval,
} from 'date-fns';
import { FormatOptions } from 'date-fns/format';
import { nl } from 'date-fns/locale';
import { DateRangePipeOptions } from '../pipes/tt-date-range/date-range-pipe-options.interface';
import { DateRangeInterface } from './date-range/date-range.interface';
import { RangeModel } from './range.model';

export abstract class BaseDateRange<
  StartBoundary extends 'finite' | 'infinite' = 'finite',
  EndBoundary extends 'finite' | 'infinite' = 'finite',
  RangeType extends Date = Date,
  PlainType extends string | number = string | number,
  StartType extends RangeType | null = StartBoundary extends 'finite'
    ? RangeType
    : RangeType | null,
  EndType extends RangeType | null = EndBoundary extends 'finite' ? RangeType : RangeType | null,
  PlainStartType extends PlainType | null = StartBoundary extends 'finite'
    ? PlainType
    : PlainType | null,
  PlainEndType extends PlainType | null = EndBoundary extends 'finite'
    ? PlainType
    : PlainType | null,
>
  extends RangeModel<
    StartBoundary,
    EndBoundary,
    PlainType,
    RangeType,
    StartType,
    EndType,
    PlainStartType,
    PlainEndType,
    DateRangePipeOptions
  >
  implements DateRangeInterface<StartBoundary, EndBoundary, RangeType, StartType, EndType>
{
  /**
   * @inheritDoc
   */
  public contains(date: Date): boolean {
    if (this.min === null && this.max === null) {
      return true;
    }

    if (this.min === null) {
      return isBefore(date, this.max as RangeType) || isEqual(date, this.max as RangeType);
    }

    if (this.max === null) {
      return isAfter(date, this.min as RangeType) || isEqual(date, this.min as RangeType);
    }

    return isWithinInterval(date, this.interval);
  }

  /**
   * @inheritDoc
   */
  public override format(
    formatString: string,
    divider: string = ' - ',
    options?: FormatOptions,
  ): string {
    if (!options) {
      options = { locale: nl };
    }

    if (formatString) {
      const start = this.min ? fnsFormat(this.min, formatString, options) : '';
      const end = this.max ? fnsFormat(this.max, formatString, options) : '';
      return `${start}${divider}${end}`;
    }

    return this.humanReadableString(divider, formatString);
  }

  /**
   * Returns the duration in minutes.
   */
  public duration(): number {
    if (this.min === null || this.max === null) {
      return Infinity;
    }
    return differenceInMinutes(this.min, this.max);
  }

  /**
   * Converts the given timeRange into a DateFns interval.
   */
  public get interval(): Interval {
    if (this.min === null || this.max === null) {
      throw new Error('Cannot create interval from infinite range');
    }
    return { start: this.min, end: this.max };
  }

  public get materialDateRange(): MaterialDateRange<Date> {
    return new MaterialDateRange(this.min as unknown as Date, this.max as unknown as Date);
  }
}
